import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./DashboardLogin.css";

const DashboardLogin = () => {

  const navigate = useNavigate();

  /* ============================
            STATES
  ============================ */

  const [email, setEmail] = useState("");

  // OTP as 6 separate boxes
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);

  const [loading, setLoading] = useState(false);

  const [timer, setTimer] = useState(0);

  const [error, setError] = useState("");

  // email | otp | logged
  const [step, setStep] = useState("email");

  const [service, setService] = useState("");

  /* ============================
          LOCAL STORAGE
  ============================ */

  const isLoggedIn = localStorage.getItem("rg_auth") === "1";

  /* ============================
          INITIAL LOAD
  ============================ */

  useEffect(() => {

    const getService = async () => {
      try {
        const storedEmail = localStorage.getItem("user_email");

        if (!storedEmail) return;

        const res = await axios.get(`/api/user-service/${storedEmail}`);

        if (res.data.success) {
          setService(res.data.service);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (isLoggedIn) {
      const savedEmail = localStorage.getItem("rg_email");
      if (savedEmail) setEmail(savedEmail);

      const savedService = localStorage.getItem("service");
      if (savedService) setService(savedService);

      setStep("logged");
      getService();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================
      OTP TIMER
  ============================ */

  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  /* ============================
      OTP INPUT
  ============================ */

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  /* ============================
      SEND OTP
  ============================ */

  const handleSendOtp = async () => {
    setError("");

    if (!email.trim()) {
      setError("Enter valid email");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post("/auth/send-otp", {
        email: email.trim().toLowerCase()
      });

      if (res.data.success) {
        setStep("otp");
        setTimer(30);
        setOtp(["", "", "", "", "", ""]);
      }
    } catch (err) {
      setError(err.response?.data?.error || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  /* ============================
      VERIFY OTP
  ============================ */

  const handleVerifyOtp = async () => {
    setError("");

    const enteredOtp = otp.join("");

    if (enteredOtp.length !== 6) {
      setError("Enter valid OTP");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post("/auth/verify-otp", {
        email,
        otp: enteredOtp
      });

      if (res.data.success) {
        localStorage.setItem("rg_auth", "1");
        localStorage.setItem("rg_email", email);
        localStorage.setItem("user_email", email);
        localStorage.setItem("service", res.data.service || "");

        setService(res.data.service || "");

        await axios.post("/track-dashboard-login", { email });

        setStep("logged");
      }
    } catch (err) {
      setError(err.response?.data?.error || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  /* ============================
      RESEND OTP
  ============================ */

  const resendOtp = () => {
    if (timer === 0) {
      handleSendOtp();
    }
  };

  /* ============================
      LOGOUT
  ============================ */

  const handleLogout = () => {
    localStorage.clear();
    setEmail("");
    setOtp(["", "", "", "", "", ""]);
    setService("");
    setStep("email");
  };

  /* ============================
      GO TO DASHBOARD
  ============================ */

  const goToDashboard = () => {
    navigate("/member-dashboard");
  };

  /* ============================
      RETURN
  ============================ */

  return (
    <div className="login-page">

      {/* ==========================
              LEFT SIDE
      ========================== */}

      <div className="login-left">

        <div className="brand">
          <img src="/logo.png" alt="Forge Your Peak" className="brand-logo" />
          <span>Forge Your Peak</span>
        </div>

        <h1>
          Train Smarter.
          <br />
          <span>Perform Better.</span>
        </h1>

        <p className="hero-description">
          Unlock your full potential with AI-powered vision assessments,
          reaction training and performance analytics.
        </p>

        <div className="feature-grid">

          <div className="feature-card">
            <div className="feature-icon">👁️</div>
            <h4>Vision</h4>
            <p>Assessments</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h4>Reaction</h4>
            <p>Training</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h4>Reports</h4>
            <p>Performance</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🏆</div>
            <h4>Leaderboard</h4>
            <p>Rankings</p>
          </div>

        </div>

        <img src="/Hero/runner.png" alt="Athlete" className="runner-image" />

      </div>

      {/* ==========================
              RIGHT SIDE
      ========================== */}

      <div className="login-right">

        <div className="login-card">

          {/* ==========================
              EMAIL STEP
          ========================== */}

          {step === "email" && (
            <>
              <div className="login-icon">🔐</div>

              <h2>Welcome Back</h2>

              <p className="login-subtitle">
                Sign in to continue your journey.
              </p>

              <label>Email Address</label>

              <input
                type="email"
                className="login-input"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              {error && <div className="error-message">{error}</div>}

              <button
                className="primary-btn"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>

              <div className="secure-text">🔒 Secure OTP Authentication</div>
            </>
          )}

          {/* ==========================
              OTP STEP
          ========================== */}

          {step === "otp" && (
            <>
              <div className="login-icon success">✓</div>

              <h2>Verify OTP</h2>

              <p className="login-subtitle">
                Enter the 6-digit code sent to
                <br />
                <strong>{email}</strong>
              </p>

              <div className="otp-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    className="otp-input"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  />
                ))}
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                className="primary-btn"
                onClick={handleVerifyOtp}
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>

              <div className="resend-text">
                {timer > 0 ? (
                  <span>Resend OTP in {timer}s</span>
                ) : (
                  <button className="resend-btn" onClick={resendOtp}>
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          )}

          {/* ==========================
              LOGGED-IN STEP
          ========================== */}

          {step === "logged" && (
            <>
              <div className="login-icon success">✓</div>

              <h2>Welcome back!</h2>

              <p className="login-subtitle">
                You're signed in as
                <br />
                <strong>{email}</strong>
              </p>

              {service && (
                <p className="login-subtitle">
                  Active plan: <strong>{service}</strong>
                </p>
              )}

              <button className="primary-btn" onClick={goToDashboard}>
                Go to Dashboard
              </button>

              <button className="resend-btn" onClick={handleLogout}>
                Logout
              </button>
            </>
          )}

        </div>

      </div>

    </div>
  );

};

export default DashboardLogin;
